import { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import * as Sentry from '@sentry/react';
import { useCreateOnrampSessionMutation } from '../../redux/api/authorized/stripe';
import { useSolana } from '../../hooks/useSolana';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled, { keyframes } from 'styled-components';

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  margin-bottom: 20px;
`;

const Container = styled.div`
  width: 100%;
  max-width: 450px;
  min-height: 520px;
  overflow: hidden;
`;

const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
`;

const SkeletonWrapper = styled.div`
  width: 100%;
  max-width: 450px;
  min-height: 520px;
  background: ${({ theme }) => theme.text.primary};
  border-radius: 12px;
  padding: 20px;
  opacity: ${(props) => (props.isHidden ? 0 : 1)};
  transition: opacity 0.2s ease-in-out;
`;

const SkeletonHeader = styled.div`
  width: 60%;
  height: 28px;
  background: ${({ theme }) => theme.background.secondary};
  border-radius: 6px;
  margin-bottom: 20px;
  animation: ${pulse} 1.5s infinite;
`;

const SkeletonContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SkeletonInput = styled.div`
  width: 100%;
  height: 44px;
  background: ${({ theme }) => theme.background.secondary};
  border-radius: 6px;
  animation: ${pulse} 1.5s infinite;
`;

const SkeletonButton = styled.div`
  width: 100%;
  height: 44px;
  background: ${({ theme }) => theme.background.secondary};
  border-radius: 6px;
  margin-top: 4px;
  animation: ${pulse} 1.5s infinite;
`;

const ErrorContainer = styled.div`
  width: 100%;
  max-width: 450px;
  min-height: 200px;
  background: ${({ theme }) => theme.text.primary};
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 16px;
`;

const RetryButton = styled.button`
  padding: 12px 24px;
  background: ${({ theme }) => theme.primary.primary};
  color: ${({ theme }) => theme.background.primary};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.2s;

  @media (hover: hover) {
    &:hover {
      background: ${({ theme }) => theme.primary.accent};
    }
  }
`;

const LoadingSkeleton = () => (
  <SkeletonWrapper>
    <SkeletonHeader />
    <SkeletonContent>
      <SkeletonInput />
      <SkeletonInput />
      <SkeletonButton />
    </SkeletonContent>
  </SkeletonWrapper>
);

const StripeEmbed = ({ destinationAmount, onSuccess, onError }) => {
  const [stripeOnramp, setStripeOnramp] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [onrampSession, setOnrampSession] = useState(null);
  const [createOnrampSession, { isLoading, error: sessionError }] =
    useCreateOnrampSessionMutation();
  const [initializationError, setInitializationError] = useState(null);
  const [mountError, setMountError] = useState(null);
  const [isMounting, setIsMounting] = useState(false);
  const { wallets } = useSolana();

  const navigate = useNavigate();

  useEffect(() => {
    const initializeStripe = async () => {
      try {
        await loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

        if (!window.StripeOnramp) {
          const errorMessage =
            'Stripe Onramp not available. Please refresh the page or try a different payment method.';
          console.error(errorMessage);
          Sentry.captureException(new Error(errorMessage), {
            tags: {
              component: 'StripeEmbed',
              action: 'initializeStripe',
            },
          });
          setInitializationError(errorMessage);
          if (onError) onError(new Error(errorMessage));
          return;
        }

        const onramp = window.StripeOnramp(
          import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY,
        );
        setStripeOnramp(onramp);
        setInitializationError(null);
      } catch (err) {
        const errorMessage = err.message || 'Failed to initialize Stripe';
        console.error('Stripe initialization error:', err);
        Sentry.captureException(err, {
          tags: {
            component: 'StripeEmbed',
            action: 'initializeStripe',
          },
          extra: {
            destinationAmount,
          },
        });
        setInitializationError(errorMessage);
        if (onError) onError(err);
      }
    };

    initializeStripe();
  }, [sessionId, onError]);

  useEffect(() => {
    const createSession = async () => {
      try {
        const response = await createOnrampSession({
          destinationNetwork: 'solana',
          destinationAmount: destinationAmount || undefined,
        }).unwrap();

        setClientSecret(response.clientSecret);
        setSessionId(response.sessionId);
      } catch (err) {
        console.error('Error creating onramp session:', err);
        Sentry.captureException(err, {
          tags: {
            component: 'StripeEmbed',
            action: 'createSession',
          },
          extra: {
            destinationAmount,
            destinationNetwork: 'solana',
          },
        });
        if (onError) onError(err);
      }
    };

    if (wallets && wallets.length > 0 && !sessionId) {
      createSession();
    }
  }, [createOnrampSession, destinationAmount, wallets, onError, sessionId]);

  useEffect(() => {
    if (!onrampSession) return;

    const handleSessionUpdated = (event) => {
      if (event.payload?.session?.status === 'fulfillment_complete') {
        onSuccess ? onSuccess() : navigate(`/app/wallet`);
      } else if (
        ['canceled', 'expired'].includes(event.payload?.session?.status)
      ) {
        toast('Payment expired. Refresh the page to start a new payment.', {
          type: 'error',
        });
      }
    };
    onrampSession.addEventListener(
      'onramp_session_updated',
      handleSessionUpdated,
    );

    return () => {
      onrampSession.removeEventListener(
        'onramp_session_updated',
        handleSessionUpdated,
      );
    };
  }, [onrampSession, sessionId, onSuccess]);

  useEffect(() => {
    if (!stripeOnramp || !clientSecret) return;

    let isActive = true;

    const mountOnramp = async () => {
      if (!isActive) return;
      setIsMounting(true);
      try {
        const session = stripeOnramp.createSession({ clientSecret });
        if (!isActive) return;
        setOnrampSession(session);
        setMountError(null);

        const waitForContainer = () => {
          return new Promise((resolve) => {
            const container = document.getElementById(
              'stripe-onramp-container',
            );
            if (container) {
              resolve(container);
            } else {
              setTimeout(() => waitForContainer().then(resolve), 100);
            }
          });
        };

        const container = await waitForContainer();
        if (!container) {
          const containerError = new Error('Stripe container not found');
          Sentry.captureException(containerError, {
            tags: {
              component: 'StripeEmbed',
              action: 'mountOnramp',
            },
          });
          throw containerError;
        }

        if (!isActive) return;
        await session.mount('#stripe-onramp-container');
        if (!isActive) return;
        setIsMounting(false);

        window.addEventListener('error', async (event) => {
          if (!isActive) return;
          if (
            event.target.src &&
            (event.target.src.includes('stripe.com') ||
              event.target.src.includes('crypto-js.stripe.com'))
          ) {
            console.error('Stripe script error:', event);
            const errorMessage =
              'Error loading payment options. Please refresh the page to try again.';
            Sentry.captureException(new Error(errorMessage), {
              tags: {
                component: 'StripeEmbed',
                action: 'stripeScriptError',
              },
              extra: {
                scriptSrc: event.target.src,
                errorType: event.type,
              },
            });
            setMountError(errorMessage);
            setIsMounting(false);
            toast(errorMessage, {
              type: 'error',
            });
          }
        });
      } catch (err) {
        if (!isActive) return;
        const errorMessage = err.message || 'Error loading payment options';
        console.error('Error mounting Stripe Onramp:', err);
        Sentry.captureException(err, {
          tags: {
            component: 'StripeEmbed',
            action: 'mountOnramp',
          },
          extra: {
            clientSecret: clientSecret ? 'present' : 'missing',
            stripeOnramp: stripeOnramp ? 'present' : 'missing',
          },
        });
        setMountError(errorMessage);
        setIsMounting(false);
        toast(errorMessage, {
          type: 'error',
        });
      }
    };

    setTimeout(mountOnramp, 100);

    return () => {
      isActive = false;
      if (onrampSession) {
        try {
          onrampSession.unmount();
        } catch (err) {
          console.error('Error unmounting Stripe Onramp:', err);
        }
      }

      const container = document.getElementById('stripe-onramp-container');
      if (container) {
        container.innerHTML = '';
      }
      setIsMounting(false);
    };
  }, [stripeOnramp, clientSecret, onError]);

  if (isLoading || isMounting) {
    return (
      <Wrapper>
        <LoadingSkeleton />
      </Wrapper>
    );
  }

  if (sessionError || initializationError || mountError) {
    const errorMessage =
      sessionError?.data?.message ||
      initializationError ||
      mountError ||
      'Error loading payment options. Please try again later.';
    return (
      <Wrapper>
        <ErrorContainer>
          <p>{errorMessage}</p>
          <RetryButton onClick={() => window.location.reload()}>
            Retry
          </RetryButton>
        </ErrorContainer>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Container id='stripe-onramp-container' />
    </Wrapper>
  );
};

export default StripeEmbed;
